// Display formatting. All dates render in the viewer's locale and zone.

export function cn(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ')
}

const DAY = 24 * 60 * 60 * 1000

function toDate(iso: string | null | undefined): Date | null {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d
}

/** "5 Oct 2026" */
export function formatDate(iso: string | null | undefined, fallback = '—'): string {
  const d = toDate(iso)
  return d ? d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : fallback
}

/** "1 Nov" (same year) or "1 Nov 2027" */
export function formatShortDate(iso: string | null | undefined, now: Date = new Date(), fallback = '—'): string {
  const d = toDate(iso)
  if (!d) return fallback
  return d.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
  })
}

/** "5 Oct 2026, 14:03" */
export function formatDateTime(iso: string | null | undefined, fallback = '—'): string {
  const d = toDate(iso)
  return d
    ? d.toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : fallback
}

/** "in 3 days", "2 days ago", "today" */
export function formatRelativeDays(iso: string | null | undefined, now: number = Date.now()): string {
  const d = toDate(iso)
  if (!d) return '—'
  const days = Math.round((d.getTime() - now) / DAY)
  if (days === 0) return 'today'
  if (days === 1) return 'tomorrow'
  if (days === -1) return 'yesterday'
  return days > 0 ? `in ${days} days` : `${-days} days ago`
}

const REL_UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 365 * 24 * 3600],
  ['month', 30 * 24 * 3600],
  ['week', 7 * 24 * 3600],
  ['day', 24 * 3600],
  ['hour', 3600],
  ['minute', 60],
]

/** "3 hours ago", "in 2 days", "just now". */
export function relativeTime(iso: string, now: number = Date.now()): string {
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return '—'
  const diff = Math.round((t - now) / 1000)
  const abs = Math.abs(diff)
  if (abs < 45) return 'just now'
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
  for (const [unit, secs] of REL_UNITS) {
    if (abs >= secs) return rtf.format(Math.round(diff / secs), unit)
  }
  return rtf.format(Math.round(diff / 60), 'minute')
}

/** Whole days from now until `iso` (negative = past), rounded up; null if none. */
export function daysUntil(iso: string | null | undefined, now: number = Date.now()): number | null {
  const d = toDate(iso)
  if (!d) return null
  return Math.ceil((d.getTime() - now) / DAY)
}

/** "YYYY-MM-DD" for a Date in the viewer's zone (for <input type="date">). */
export function toDateInput(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

/** The viewer's IANA zone, e.g. "America/New_York" (UTC if unknown). */
export function viewerTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

/** Public URL for a host ("ironbridge.scorelm.com" → "https://ironbridge.scorelm.com"). */
export function hostUrl(host: string): string {
  return /^https?:\/\//.test(host) ? host : `https://${host}`
}

export function formatCount(n: number): string {
  return n.toLocaleString()
}

/** "basketball" → "Basketball" */
export function titleCase(s: string): string {
  return s
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(' ')
}
